import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CForm } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilUser, cilArrowLeft, cilLockLocked } from '@coreui/icons'
import { doForgotPassword } from 'src/services/actions/auth'
import { toast } from 'src/components/common/ToastComponent/toast'
import whiteLogo from 'src/assets/images/Eurocharged-logo-white.png'
import bgImage from 'src/assets/images/logIn.jpg'

const ForgotPassword = () => {
  const navigate = useNavigate()
  const [userName, setUserName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [userFocus, setUserFocus] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [submittedUser, setSubmittedUser] = useState('')

  const handleChange = (value) => {
    setUserName(value)
  }

  const handleResetClick = async () => {
    if (!userName.trim()) {
      toast.show({
        title: 'Warning',
        description: 'Please enter your username or email!',
      })
      return
    }

    setIsLoading(true)
    try {
      const trimmedUser = userName.trim()
      await doForgotPassword({
        userName: trimmedUser,
        userType: 'admin',
      })
      setSubmittedUser(trimmedUser)
      setIsSuccessModalOpen(true)
    } catch (err) {
      toast.show({
        title: 'Warning',
        description: err?.response?.data?.data?.message ? err?.response?.data?.data?.message : 'No user found with those credentials!',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.which === 13) {
      handleResetClick()
    }
  }

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        margin: 0,
        padding: '16px',
        boxSizing: 'border-box',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        backgroundColor: '#07090e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      {/* Isolated Background Scene (prevents any particle or orb blur from causing page scroll) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {/* 1. Crisp High-Res Wallpaper Layer */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `linear-gradient(135deg, rgba(8, 10, 16, 0.82) 0%, rgba(13, 17, 26, 0.72) 45%, rgba(5, 7, 11, 0.9) 100%), url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            pointerEvents: 'none',
          }}
        />

        {/* 2. Gentle Ambient Cyan Atmospheric Lighting Glow */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 30%, rgba(0, 151, 178, 0.25) 0%, transparent 65%)',
            animation: 'auroraPulse 10s ease-in-out infinite alternate',
            pointerEvents: 'none',
          }}
        />

        {/* 3. Moving Cyber Matrix Grid Overlay */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(rgba(0, 151, 178, 0.16) 1.2px, transparent 1.2px)',
            backgroundSize: '34px 34px',
            animation: 'gridScroll 22s linear infinite',
            pointerEvents: 'none',
          }}
        />

        {/* 4. Animated Floating Cyan Particles */}
        {[
          { left: '8%', size: '3px', duration: '9s', delay: '0s', opacity: 0.65 },
          { left: '20%', size: '4px', duration: '13s', delay: '3.5s', opacity: 0.55 },
          { left: '33%', size: '2px', duration: '8.5s', delay: '1s', opacity: 0.7 },
          { left: '46%', size: '5px', duration: '15s', delay: '4.5s', opacity: 0.45 },
          { left: '60%', size: '3px', duration: '11s', delay: '2s', opacity: 0.65 },
          { left: '74%', size: '4px', duration: '14s', delay: '5.5s', opacity: 0.5 },
          { left: '86%', size: '3px', duration: '10s', delay: '1.2s', opacity: 0.7 },
          { left: '94%', size: '2px', duration: '12s', delay: '6.5s', opacity: 0.55 },
          { left: '14%', size: '2px', duration: '10.5s', delay: '5s', opacity: 0.6 },
          { left: '81%', size: '4px', duration: '9.2s', delay: '3s', opacity: 0.65 },
        ].map((p, idx) => (
          <div
            key={idx}
            style={{
              position: 'absolute',
              left: p.left,
              bottom: '-10px',
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              backgroundColor: idx % 2 === 0 ? '#4ad0cd' : '#0097b2',
              boxShadow: `0 0 10px 2px ${idx % 2 === 0 ? '#4ad0cd' : '#0097b2'}`,
              pointerEvents: 'none',
              animation: `particleFloat ${p.duration} linear infinite`,
              animationDelay: p.delay,
              opacity: p.opacity,
            }}
          />
        ))}

        {/* 5. Animated Floating Ambient Glowing Orbs */}
        <div 
          style={{
            position: 'absolute',
            top: '15%',
            left: '18%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 151, 178, 0.35) 0%, transparent 68%)',
            filter: 'blur(80px)',
            pointerEvents: 'none',
            animation: 'floatOrb1 16s ease-in-out infinite alternate',
          }}
        />
        <div 
          style={{
            position: 'absolute',
            bottom: '10%',
            right: '16%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(74, 208, 205, 0.3) 0%, transparent 70%)',
            filter: 'blur(85px)',
            pointerEvents: 'none',
            animation: 'floatOrb2 20s ease-in-out infinite alternate',
          }}
        />
      </div>

      {/* Center Frosted Glass Modal */}
      <div 
        style={{
          width: '100%',
          maxWidth: '520px',
          background: 'linear-gradient(165deg, rgba(22, 26, 36, 0.88) 0%, rgba(13, 15, 22, 0.94) 100%)',
          backdropFilter: 'blur(28px) saturate(180%)',
          WebkitBackdropFilter: 'blur(28px) saturate(180%)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.85), 0 0 50px rgba(0, 151, 178, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
          padding: '36px 38px 30px 38px',
          position: 'relative',
          overflow: 'hidden',
          boxSizing: 'border-box',
          margin: 'auto',
          zIndex: 10,
          animation: 'cardEntrance 0.75s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        }}
      >
        <style>{`
          @keyframes auroraPulse {
            0% { opacity: 0.35; transform: scale(0.96); }
            50% { opacity: 0.75; transform: scale(1.04); }
            100% { opacity: 0.35; transform: scale(0.96); }
          }
          @keyframes gridScroll {
            0% { background-position: 0px 0px; }
            100% { background-position: 34px 34px; }
          }
          @keyframes particleFloat {
            0% { transform: translateY(0) scale(0.6); opacity: 0; }
            15% { opacity: 0.85; }
            85% { opacity: 0.85; }
            100% { transform: translateY(-105vh) scale(1.3); opacity: 0; }
          }
          @keyframes floatOrb1 {
            0% { transform: translate(0px, 0px) scale(1); opacity: 0.35; }
            50% { transform: translate(60px, -40px) scale(1.15); opacity: 0.55; }
            100% { transform: translate(-30px, 30px) scale(0.95); opacity: 0.4; }
          }
          @keyframes floatOrb2 {
            0% { transform: translate(0px, 0px) scale(1); opacity: 0.25; }
            50% { transform: translate(-70px, -50px) scale(1.2); opacity: 0.45; }
            100% { transform: translate(40px, 20px) scale(1.05); opacity: 0.3; }
          }
          @keyframes cardEntrance {
            0% { opacity: 0; transform: translateY(22px) scale(0.97); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes shimmerGlow {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes fadeInOverlay {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes modalScaleUp {
            0% { opacity: 0; transform: scale(0.92) translateY(14px); }
            100% { opacity: 1; transform: scale(1) translateY(0); }
          }
          .eurocharged-auth-input:-webkit-autofill,
          .eurocharged-auth-input:-webkit-autofill:hover,
          .eurocharged-auth-input:-webkit-autofill:focus,
          .eurocharged-auth-input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px #0c1018 inset !important;
            -webkit-text-fill-color: #ffffff !important;
            caret-color: #ffffff !important;
            transition: background-color 50000s ease-in-out 0s !important;
            background-color: transparent !important;
          }
          .eurocharged-auth-input::placeholder {
            color: #64748b !important;
            opacity: 1 !important;
          }
        `}</style>

        {/* Teal Accent Shimmer Bar */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent 0%, #0097b2 25%, #4ad0cd 50%, #0097b2 75%, transparent 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmerGlow 4s ease infinite',
          }}
        />

        {/* Ambient Top Glow */}
        <div 
          style={{
            position: 'absolute',
            top: '-60px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '260px',
            height: '110px',
            background: 'radial-gradient(circle, rgba(0, 151, 178, 0.38) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        {/* Header with White Logo */}
        <div className="text-center mb-3">
          <div className="mb-2">
            <img 
              src={whiteLogo} 
              alt="Eurocharged Performance" 
              style={{ 
                height: 'auto', 
                width: '210px', 
                maxWidth: '100%', 
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.8))',
              }} 
            />
          </div>

          <div 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 14px',
              borderRadius: '9999px',
              background: 'rgba(0, 151, 178, 0.15)',
              border: '1px solid rgba(0, 151, 178, 0.35)',
              color: '#4ad0cd',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '1.8px',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}
          >
            <span 
              style={{ 
                width: '6px', 
                height: '6px', 
                borderRadius: '50%', 
                backgroundColor: '#0097b2', 
                boxShadow: '0 0 8px #0097b2' 
              }} 
            />
            Account Recovery
          </div>

          <h2 
            style={{ 
              color: '#ffffff', 
              fontSize: '1.45rem', 
              fontWeight: 700, 
              letterSpacing: '-0.3px', 
              margin: '2px 0 6px',
            }}
          >
            Forgot Password?
          </h2>
          <p 
            style={{ 
              color: '#94a3b8', 
              fontSize: '0.86rem', 
              margin: 0,
              lineHeight: 1.5,
              maxWidth: '380px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Enter your admin username or email address and we will initiate the secure reset process.
          </p>
        </div>

        {/* Form Body */}
        <CForm>
          <div className="mb-4">
            <label 
              style={{ 
                color: '#cbd5e1', 
                fontSize: '0.75rem', 
                fontWeight: 600, 
                letterSpacing: '0.6px', 
                textTransform: 'uppercase', 
                marginBottom: '6px', 
                display: 'block' 
              }}
            >
              Username or Email
            </label>
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                background: userFocus ? 'rgba(10, 13, 20, 0.95)' : 'rgba(12, 16, 24, 0.72)',
                border: userFocus ? '1px solid #0097b2' : '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '12px',
                padding: '4px 14px',
                boxShadow: userFocus ? '0 0 0 3px rgba(0, 151, 178, 0.25)' : 'none',
                transition: 'all 0.2s ease',
              }}
            >
              <CIcon 
                icon={cilUser} 
                style={{ 
                  color: userFocus ? '#4ad0cd' : '#0097b2', 
                  marginRight: '12px', 
                  width: '18px', 
                  height: '18px', 
                  flexShrink: 0 
                }} 
              />
              <input
                type="text"
                className="eurocharged-auth-input"
                placeholder="Enter your registered username or email"
                autoComplete="username"
                value={userName}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={() => setUserFocus(true)}
                onBlur={() => setUserFocus(false)}
                onKeyDown={handleKeyDown}
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: '#ffffff',
                  width: '100%',
                  padding: '12px 8px',
                  fontSize: '0.95rem',
                  borderRadius: '8px',
                }}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-3 mb-3">
            <button
              type="button"
              disabled={isLoading}
              onClick={handleResetClick}
              className="w-100 py-3 fw-bold text-uppercase d-flex align-items-center justify-content-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #0097b2 0%, #036e81 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.92rem',
                letterSpacing: '1.5px',
                boxShadow: '0 8px 24px rgba(0, 151, 178, 0.45)',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.75 : 1,
              }}
              onMouseOver={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 151, 178, 0.65)'
                  e.currentTarget.style.filter = 'brightness(1.1)'
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 151, 178, 0.45)'
                e.currentTarget.style.filter = 'brightness(1)'
              }}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  <span>Processing Request...</span>
                </>
              ) : (
                <>
                  <CIcon icon={cilLockLocked} style={{ width: '16px', height: '16px' }} />
                  <span>Send Reset Instructions</span>
                </>
              )}
            </button>
          </div>

          {/* Back to Login Link */}
          <div className="text-center mt-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                padding: '4px 8px',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = '#4ad0cd'
                e.currentTarget.style.transform = 'translateX(-3px)'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = '#94a3b8'
                e.currentTarget.style.transform = 'translateX(0)'
              }}
            >
              <CIcon icon={cilArrowLeft} style={{ width: '14px', height: '14px' }} />
              Back to Sign In
            </button>
          </div>

          {/* Secure Connection Footer */}
          <div 
            className="text-center pt-3 mt-4" 
            style={{ 
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              color: '#64748b',
              fontSize: '0.72rem',
              letterSpacing: '0.5px'
            }}
          >
            🔒 256-Bit SSL Encrypted Admin Session
          </div>
        </CForm>
      </div>

      {/* Success Popup Modal */}
      {isSuccessModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(4, 6, 10, 0.82)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 10000,
            animation: 'fadeInOverlay 0.25s ease-out forwards',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '470px',
              background: 'linear-gradient(165deg, rgba(20, 25, 36, 0.96) 0%, rgba(11, 14, 21, 0.98) 100%)',
              borderRadius: '24px',
              border: '1px solid rgba(0, 151, 178, 0.35)',
              boxShadow: '0 30px 70px rgba(0, 0, 0, 0.9), 0 0 50px rgba(0, 151, 178, 0.25)',
              padding: '38px 32px 32px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              animation: 'modalScaleUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            {/* Top Teal Shimmer Bar */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, transparent 0%, #0097b2 25%, #4ad0cd 50%, #0097b2 75%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmerGlow 3s ease infinite',
              }}
            />

            {/* Glowing Mail Icon Badge */}
            <div
              style={{
                width: '76px',
                height: '76px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(0, 151, 178, 0.22) 0%, rgba(74, 208, 205, 0.1) 100%)',
                border: '1.5px solid rgba(0, 151, 178, 0.55)',
                boxShadow: '0 0 30px rgba(0, 151, 178, 0.4), inset 0 0 15px rgba(0, 151, 178, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4ad0cd"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>

            {/* Modal Heading */}
            <h3
              style={{
                color: '#ffffff',
                fontSize: '1.45rem',
                fontWeight: 700,
                letterSpacing: '-0.3px',
                marginBottom: '10px',
              }}
            >
              Reset Link Sent!
            </h3>

            {/* Informative text */}
            <p
              style={{
                color: '#cbd5e1',
                fontSize: '0.92rem',
                lineHeight: 1.55,
                marginBottom: '12px',
              }}
            >
              A password reset email has been dispatched to the address associated with:
            </p>

            {/* Highlighted Username */}
            <div
              style={{
                display: 'inline-block',
                background: 'rgba(0, 151, 178, 0.14)',
                border: '1px solid rgba(0, 151, 178, 0.35)',
                borderRadius: '8px',
                padding: '6px 18px',
                color: '#4ad0cd',
                fontSize: '0.88rem',
                fontWeight: 600,
                marginBottom: '16px',
                letterSpacing: '0.3px',
              }}
            >
              {submittedUser}
            </div>

            <p
              style={{
                color: '#94a3b8',
                fontSize: '0.82rem',
                lineHeight: 1.5,
                marginBottom: '26px',
              }}
            >
              Please check your email inbox (and spam/junk folder) and click the link to reset your password.
            </p>

            {/* Button to go to the Sign In page */}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-100 py-3 fw-bold text-uppercase d-flex align-items-center justify-content-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #0097b2 0%, #036e81 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.92rem',
                letterSpacing: '1.5px',
                boxShadow: '0 8px 24px rgba(0, 151, 178, 0.5)',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 151, 178, 0.7)'
                e.currentTarget.style.filter = 'brightness(1.1)'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 151, 178, 0.5)'
                e.currentTarget.style.filter = 'brightness(1)'
              }}
            >
              <CIcon icon={cilLockLocked} style={{ width: '16px', height: '16px' }} />
              <span>Go to Sign In Page</span>
            </button>

            {/* Option to try again / resend */}
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setIsSuccessModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  padding: '4px',
                  textDecoration: 'underline',
                  transition: 'color 0.2s',
                }}
                onMouseOver={(e) => (e.target.style.color = '#ffffff')}
                onMouseOut={(e) => (e.target.style.color = '#94a3b8')}
              >
                Didn't receive the email? Try again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ForgotPassword
